import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ChevronDown, ChevronUp, CircleX, ExternalLink, Play } from 'lucide-react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'src/componentes/dropdown';
import { differenceInCalendarDays, format } from 'date-fns';
import { useSnackbar } from 'notistack';

import Alert from 'src/componentes/message-box/alert';
import CriticalConfirm from 'src/componentes/message-box/critical-confirm';
import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import LoadingContainer from 'src/componentes/loading-container';
import Panel from 'src/componentes/panel';
import useAuth from 'src/hooks/useAuth';
import formatDateFns from 'src/utils/formatDateFns';
import { ParseError } from 'src/services/downloadFileUrl';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import { capitalizeFirstLetter } from 'src/utils/string';
import {
  getDataAplicacaoFormularioGlobal,
  getLookupId,
  getPesquisaClimaLink,
  SITUACAO_APLICACAO_FORMULARIO_GLOBAL,
} from 'src/paginas/avaliacao-desempenho/PesquisaClima/utils';
import RespostasFiltroDrawer from './RespostasFiltroDrawer';
import RespostasList from './RespostasList';
import AplicacaoStatusBadge from '../AplicacaoStatusBadge';

const createEmptyFiltro = () => ({
  areas: null,
  considerarAreasSubordinadas: false,
  colaboradoresGrupos: null,
  dataEnvioInicio: null,
  dataEnvioFim: null,
});

function RespostasPesquisaClimaTab({
  active,
  aplicacaoEmAndamento = false,
  aplicacaoRefreshToken,
  ehConfidencial = false,
  entidadeId,
  filtroDrawerFocusTrapPriority,
  onAplicacaoAlterada,
  podeEncerrar = false,
  podeIniciarProximaAplicacao = false,
  periodoDisponivelRespostaDias,
}) {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { terms: resources, user } = useAuth();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);
  const hasLoadedRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUsos, setIsLoadingUsos] = useState(true);
  const [filtro, setFiltro] = useState(createEmptyFiltro());
  const [filtroAplicado, setFiltroAplicado] = useState(createEmptyFiltro());
  const [filterOpen, setFilterOpen] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [usos, setUsos] = useState([]);
  const [usoAbertoId, setUsoAbertoId] = useState(null);
  const [usosVisitados, setUsosVisitados] = useState([]);

  const usoVigente = usos.find(
    (uso) =>
      getLookupId(uso.situacao) === SITUACAO_APLICACAO_FORMULARIO_GLOBAL.VIGENTE
  );
  const proximoUsoPrevisto = [...usos]
    .filter(
      (uso) =>
        getLookupId(uso.situacao) ===
        SITUACAO_APLICACAO_FORMULARIO_GLOBAL.PREVISTA
    )
    .sort((a, b) => {
      const dataA = getDataAplicacaoFormularioGlobal(a.dataInicio)?.getTime();
      const dataB = getDataAplicacaoFormularioGlobal(b.dataInicio)?.getTime();
      return dataA - dataB;
    })[0];
  const podeEncerrarAplicacao = !!podeEncerrar && !!usoVigente;
  const podeIniciarAplicacao = !!podeIniciarProximaAplicacao;

  const isEntidadePersisted = !!entidadeId;
  const sanitizeFiltro = (currentFiltro) =>
    ehConfidencial
      ? {
          ...currentFiltro,
          colaboradoresGrupos: null,
        }
      : currentFiltro;
  const filtroEfetivo = sanitizeFiltro(filtro);
  const filtroAplicadoEfetivo = sanitizeFiltro(filtroAplicado);

  useEffect(() => {
    setUsosVisitados([]);
  }, [entidadeId]);

  useEffect(() => {
    if (!active || !entidadeId) {
      setUsoAbertoId(null);
      setFilterOpen(false);
      return;
    }

    hasLoadedRef.current = true;
    let cancelled = false;
    setUsoAbertoId(null);

    const carregarUsos = async () => {
      setIsLoadingUsos(true);

      try {
        const response = await http.get(
          '/FormularioGlobalRespostas/ObterAplicacoes',
          { params: { entidadeId } }
        );
        const dados = response.data?.model || response.data || [];
        const aplicacoes = Array.isArray(dados)
          ? dados
          : dados.aplicacoes || dados.usos || [];
        const usosCarregados = aplicacoes
          .filter((uso) => uso?.formularioUsoId)
          .sort((a, b) => {
            const dataA =
              getDataAplicacaoFormularioGlobal(a.dataInicio)?.getTime() || 0;
            const dataB =
              getDataAplicacaoFormularioGlobal(b.dataInicio)?.getTime() || 0;

            return dataB - dataA || b.formularioUsoId - a.formularioUsoId;
          });

        if (cancelled) return;

        setUsos(usosCarregados);
      } catch (err) {
        if (cancelled) return;

        errorHandler(err);
        setUsos([]);
        setUsoAbertoId(null);
      } finally {
        if (!cancelled) setIsLoadingUsos(false);
      }
    };

    carregarUsos();

    return () => {
      cancelled = true;
    };
  }, [active, entidadeId, aplicacaoRefreshToken]);

  const handleChangeFiltro = (field, value) => {
    setFiltro((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'areas' && (!value || value.length === 0)
        ? { considerarAreasSubordinadas: false }
        : {}),
    }));
  };

  const handleAplicarFiltro = () => {
    const filtroSanitizado = sanitizeFiltro(filtro);

    setFiltro(filtroSanitizado);
    setFiltroAplicado(filtroSanitizado);
    setRefreshToken((prev) => prev + 1);
    setFilterOpen(false);
  };

  const handleClearFiltro = () => {
    const vazio = createEmptyFiltro();
    setFiltro(vazio);
    setFiltroAplicado(vazio);
    setRefreshToken((prev) => prev + 1);
  };

  const handleRemoveFiltro = (field) => {
    const removerCampo = (prev) => ({
      ...prev,
      [field]: createEmptyFiltro()[field],
      ...(field === 'areas' ? { considerarAreasSubordinadas: false } : {}),
    });

    setFiltro(removerCampo);
    setFiltroAplicado(removerCampo);
    setRefreshToken((prev) => prev + 1);
  };

  const handleLinkHistoricoExportacao = () => {
    window.open('/ferramentas/historico-exportacaojob', '_blank');
  };

  const renderMensagemExportacaoEmSegundoPlano = () => {
    ContentManager.addContent(
      <Alert
        closeModal
        title={intl.formatMessage({ id: 'exportacao' })}
        message={
          <div>
            <div>
              {intl.formatMessage({
                id: 'exportacaoEstaSendoProcessadaEmSegundoPlano',
              })}
            </div>
            <div className="d-flex align-items-center mt-2">
              <div className="mr-2">
                {intl.formatMessage({
                  id: 'vocePodeAcompanharAndamentoFazerDownloadNo',
                })}
                :
              </div>
              <Button
                color="outline-primary"
                leftIcon={ExternalLink}
                onClick={handleLinkHistoricoExportacao}
              >
                {intl.formatMessage({ id: 'acompanharExportacao' })}
              </Button>
            </div>
          </div>
        }
      />
    );
  };

  const handleExportar = async (formularioUsoId) => {
    setIsLoading(true);
    try {
      await http.post('/FormularioGlobalRespostas/ExportarRespostas', {
        entidadeId,
        formularioUsoId,
        areas: filtroAplicadoEfetivo.areas,
        considerarAreasSubordinadas:
          filtroAplicadoEfetivo.considerarAreasSubordinadas,
        colaboradoresGrupos: filtroAplicadoEfetivo.colaboradoresGrupos,
        dataEnvioInicio: filtroAplicadoEfetivo.dataEnvioInicio,
        dataEnvioFim: filtroAplicadoEfetivo.dataEnvioFim,
      });

      renderMensagemExportacaoEmSegundoPlano();
    } catch (err) {
      errorHandler(ParseError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const renderBlockedMessage = () => (
    <div className="d-flex align-items-center justify-content-center text-muted py-5">
      {intl.formatMessage(
        { id: 'labelSalvePesquisaClimaAntesVisualizarRespostas' },
        { pesquisa: resources.pesquisa }
      )}
    </div>
  );

  const formatarDataUso = (data) => {
    const dataUso = getDataAplicacaoFormularioGlobal(data);

    return dataUso && !Number.isNaN(dataUso.getTime())
      ? format(dataUso, currentDisplay, { locale: currentLocale })
      : '-';
  };

  const handleAlterarAplicacao = async (endpoint, mensagemSucesso, iniciou) => {
    setIsLoading(true);

    try {
      const response = await http.post(endpoint, { id: entidadeId });
      enqueueSnackbar(intl.formatMessage({ id: mensagemSucesso }), {
        variant: 'success',
      });
      onAplicacaoAlterada?.(
        iniciou ? getPesquisaClimaLink(response.data) : null
      );
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIniciarAplicacao = () => {
    if (!podeIniciarAplicacao || isLoading) return;

    const dataInicio = getDataAplicacaoFormularioGlobal(
      proximoUsoPrevisto?.dataInicio
    );
    const dataTermino = getDataAplicacaoFormularioGlobal(
      proximoUsoPrevisto?.dataTermino
    );
    const diasPrevistos =
      dataInicio && dataTermino
        ? differenceInCalendarDays(dataTermino, dataInicio) + 1
        : NaN;
    const dias =
      Number.isFinite(diasPrevistos) && diasPrevistos > 0
        ? diasPrevistos
        : Math.max(1, Number(periodoDisponivelRespostaDias) || 1);

    ContentManager.addContent(
      <CriticalConfirm
        ignoreResourceTitle
        textCapitalizeTitle={false}
        titleCapitalizeNone
        title={intl.formatMessage({ id: 'labelIniciarProximaAplicacao' })}
        message={
          <>
            <p>
              {intl.formatMessage(
                {
                  id:
                    aplicacaoEmAndamento || usoVigente
                      ? 'msgConfirmarInicioProximaAplicacaoEncerrandoAtual'
                      : 'msgConfirmarInicioProximaAplicacao',
                },
                { dias }
              )}
            </p>
            {proximoUsoPrevisto && (
              <p className="text-muted mb-0">
                {intl.formatMessage({ id: 'labelProximaAplicacaoPrevista' })}:{' '}
                {formatarDataUso(proximoUsoPrevisto.dataInicio)} –{' '}
                {formatarDataUso(proximoUsoPrevisto.dataTermino)}
              </p>
            )}
            <p className="text-muted mt-2 mb-0">
              {intl.formatMessage({
                id: 'msgAlteracoesNaoSalvasDescartadas',
              })}
            </p>
          </>
        }
        labelConfirm={intl.formatMessage({
          id: 'labelIniciarProximaAplicacao',
        })}
        handleConfirm={() =>
          handleAlterarAplicacao(
            '/PesquisaClima/IniciarProximaAplicacao',
            'msgAplicacaoIniciadaComSucesso',
            true
          )
        }
      />
    );
  };

  const handleEncerrarAplicacao = () => {
    if (!podeEncerrarAplicacao || isLoading) return;

    ContentManager.addContent(
      <CriticalConfirm
        ignoreResourceTitle
        textCapitalizeTitle={false}
        titleCapitalizeNone
        title={intl.formatMessage({ id: 'labelEncerrarAplicacao' })}
        message={
          <>
            <p>
              {intl.formatMessage({
                id: 'msgConfirmarEncerramentoAplicacao',
              })}
            </p>
            <p className="text-muted mb-0">
              {intl.formatMessage({
                id: 'msgAlteracoesNaoSalvasDescartadas',
              })}
            </p>
          </>
        }
        labelConfirm={intl.formatMessage({ id: 'labelEncerrarAplicacao' })}
        handleConfirm={() =>
          handleAlterarAplicacao(
            '/PesquisaClima/Encerrar',
            'msgAplicacaoEncerradaComSucesso',
            false
          )
        }
      />
    );
  };

  const renderMenuAplicacoes = () => (
    <UncontrolledDropdown>
      <DropdownToggle
        color="default"
        size="sm"
        toolbar
        icon="EllipsisVertical"
        type="button"
        disabled={
          isLoading || (!podeEncerrarAplicacao && !podeIniciarAplicacao)
        }
        aria-label={intl.formatMessage({ id: 'acoes' })}
        title={intl.formatMessage({ id: 'acoes' })}
      />
      <DropdownMenu>
        <DropdownItem
          disabled={!podeIniciarAplicacao}
          onClick={handleIniciarAplicacao}
        >
          <Play strokeWidth={1.75} />
          {intl.formatMessage({ id: 'labelIniciarProximaAplicacao' })}
        </DropdownItem>
        <DropdownItem
          disabled={!podeEncerrarAplicacao}
          onClick={handleEncerrarAplicacao}
        >
          <CircleX strokeWidth={1.75} />
          {intl.formatMessage({ id: 'labelEncerrarAplicacao' })}
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );

  const renderUso = (uso) => {
    const situacaoId = getLookupId(uso.situacao);
    const vigente = situacaoId === SITUACAO_APLICACAO_FORMULARIO_GLOBAL.VIGENTE;
    const encerrado =
      situacaoId === SITUACAO_APLICACAO_FORMULARIO_GLOBAL.ENCERRADA;
    const permiteRespostas = vigente || encerrado;
    const aberto = usoAbertoId === uso.formularioUsoId;
    const jaVisitado = usosVisitados.includes(uso.formularioUsoId);
    const titulo = intl.formatMessage(
      { id: 'labelPeriodoAplicacaoPesquisaClima' },
      {
        dataInicio: formatarDataUso(uso.dataInicio),
        dataTermino: formatarDataUso(uso.dataTermino),
      }
    );
    const headerContent = (
      <>
        <span className="font-weight-bold mr-3">{titulo}</span>
        {permiteRespostas && uso.quantidadeRespostas != null && (
          <span className="text-muted mr-auto">
            {uso.quantidadeRespostas}{' '}
            {intl.formatMessage({
              id:
                Number(uso.quantidadeRespostas) === 1
                  ? 'labelResposta'
                  : 'labelRespostas',
            }).toLowerCase()}
          </span>
        )}
        <span className="d-flex align-items-center ml-auto flex-shrink-0">
          <AplicacaoStatusBadge situacao={uso.situacao} className="mr-2" />
          {permiteRespostas &&
            (aberto ? <ChevronUp size={18} /> : <ChevronDown size={18} />)}
        </span>
      </>
    );

    return (
      <Card key={uso.formularioUsoId} className="mb-3">
        <CardHeader className="p-0">
          {!permiteRespostas ? (
            <div className="d-flex flex-wrap align-items-center px-3 py-3">
              {headerContent}
            </div>
          ) : (
            <button
              type="button"
              className="w-100 d-flex flex-wrap align-items-center text-left border-0 bg-transparent px-3 py-3"
              style={{ color: 'inherit' }}
              aria-expanded={aberto}
              aria-controls={
                jaVisitado ? `respostas-uso-${uso.formularioUsoId}` : undefined
              }
              onClick={() => {
                setFilterOpen(false);
                if (!aberto && !jaVisitado) {
                  setUsosVisitados((prev) => [...prev, uso.formularioUsoId]);
                }
                setUsoAbertoId(aberto ? null : uso.formularioUsoId);
              }}
            >
              {headerContent}
            </button>
          )}
        </CardHeader>
        {jaVisitado && permiteRespostas && (
          <CardBody
            id={`respostas-uso-${uso.formularioUsoId}`}
            style={aberto ? undefined : { display: 'none' }}
          >
            <RespostasList
              entidadeId={entidadeId}
              ehConfidencial={ehConfidencial}
              filtros={filtroAplicadoEfetivo}
              formularioUsoId={uso.formularioUsoId}
              isExporting={isLoading}
              onExportar={() => handleExportar(uso.formularioUsoId)}
              onRemoveFiltro={handleRemoveFiltro}
              onOpenFiltro={() => setFilterOpen(true)}
              refreshToken={refreshToken}
            />
          </CardBody>
        )}
      </Card>
    );
  };

  const renderRespostas = () => (
    <>
      {usos.length > 0 ? (
        usos.map(renderUso)
      ) : (
        <div className="text-muted text-center py-5">
          {intl.formatMessage({ id: 'msgNenhumaAplicacaoPesquisaClima' })}
        </div>
      )}
      <RespostasFiltroDrawer
        ehConfidencial={ehConfidencial}
        filtro={filtroEfetivo}
        focusTrapPriority={filtroDrawerFocusTrapPriority}
        onAplicarFiltro={handleAplicarFiltro}
        onChangeFiltro={handleChangeFiltro}
        onClearFiltro={handleClearFiltro}
        onClose={() => setFilterOpen(false)}
        open={filterOpen}
      />
    </>
  );

  return (
    <LoadingContainer
      isLoading={isLoading || (isEntidadePersisted && isLoadingUsos)}
    >
      <Panel
        header={capitalizeFirstLetter(
          intl.formatMessage({ id: 'labelRespostas' })
        )}
        headerAside={isEntidadePersisted ? renderMenuAplicacoes() : undefined}
      >
        {!isEntidadePersisted && renderBlockedMessage()}
        {isEntidadePersisted &&
          (active || hasLoadedRef.current) &&
          renderRespostas()}
      </Panel>
    </LoadingContainer>
  );
}

export default RespostasPesquisaClimaTab;
